import { FileEditor } from "./FileEditor";
import { IconX } from "../layout/Icons";

/**
 * Replit-style multi-tab editor. Manages an ordered list of open file
 * paths + an active path; renders one tab per open file, plus the
 * existing FileEditor for the active one.
 *
 * Tabs are pure UI — no persistence yet (intentional, matches Replit:
 * tabs reset on hard reload).
 */
export function MultiTabFileEditor({
  projectId,
  openTabs,
  activePath,
  onActivate,
  onClose,
  onCloseAll,
}: {
  projectId: string;
  openTabs: ReadonlyArray<string>;
  activePath: string | null;
  onActivate: (path: string) => void;
  onClose: (path: string) => void;
  onCloseAll: () => void;
}) {
  if (openTabs.length === 0 || !activePath) {
    return (
      <div
        style={{
          height: "100%",
          width: "100%",
          display: "grid",
          placeItems: "center",
          color: "var(--text-tertiary)",
          fontSize: 12,
          padding: 24,
          textAlign: "center",
          background: "#0F1115",
        }}
      >
        Click a file in the Library on the right to open it.
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        background: "#0F1115",
      }}
    >
      <div
        role="tablist"
        aria-label="Open files"
        style={{
          display: "flex",
          alignItems: "stretch",
          background: "#0a0c10",
          borderBottom: "1px solid var(--border-default)",
          overflowX: "auto",
          minHeight: 30,
        }}
      >
        {openTabs.map((path) => {
          const filename = path.split("/").pop() ?? path;
          const active = path === activePath;
          return (
            <div
              key={path}
              role="tab"
              aria-selected={active}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                background: active ? "#0F1115" : "transparent",
                color: active ? "var(--text-primary)" : "var(--text-tertiary)",
                borderRight: "1px solid var(--border-default)",
                borderBottom: active ? "1px solid #0F1115" : "1px solid var(--border-default)",
                fontSize: 11.5,
                fontFamily: "var(--font-mono)",
                marginBottom: -1,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
              onClick={() => onActivate(path)}
              title={path}
            >
              <span>{filename}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose(path);
                }}
                aria-label={`Close ${filename}`}
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: 4,
                  background: "transparent",
                  border: "none",
                  color: "var(--text-tertiary)",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                }}
              >
                <IconX size={10} />
              </button>
            </div>
          );
        })}
        <div style={{ flex: 1 }} />
        {openTabs.length > 1 && (
          <button
            type="button"
            onClick={onCloseAll}
            title="Close all tabs"
            style={{
              padding: "6px 10px",
              background: "transparent",
              border: "none",
              color: "var(--text-tertiary)",
              fontSize: 11,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Close all
          </button>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0 }}>
        <FileEditor
          projectId={projectId}
          path={activePath}
          onClose={() => onClose(activePath)}
        />
      </div>
    </div>
  );
}
